import React, { useState } from 'react';
import { usePageContent } from '../services/contentService';
import { submitContactForm, validateContactForm } from '../services/contactService';
import LoadingOverlay from '../components/LoadingOverlay';
import Notification from '../components/Notification';
import './Contact.css';
import SEO from '../components/SEO';

const Contact = () => {
  const { content, loading, error } = usePageContent('contact');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: 'info'
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Clear field error when user starts typing
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      subject: '',
      message: ''
    });
    setFormErrors({});
  };

  const showNotification = (message, type = 'info') => {
    setNotification({
      show: true,
      message,
      type
    });
  };

  const hideNotification = () => {
    setNotification({
      show: false,
      message: '',
      type: 'info'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous errors
    setFormErrors({});

    // Validate form on client side first
    const validation = validateContactForm(formData);
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      showNotification('Please correct the errors in your form and try again.', 'error');
      return;
    }

    // Submit form
    setIsSubmitting(true);

    try {
      const result = await submitContactForm(formData);

      if (result.success) {
        // Success - reset form and show success message
        resetForm();
        showNotification(result.message, 'success');
      } else {
        // Handle submission errors
        if (result.errors) {
          setFormErrors(result.errors);
        }
        showNotification(result.message, 'error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      showNotification('An unexpected error occurred. Please try again or contact us directly.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const containerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 1rem'
  };

  if (loading || !content) {
    return <LoadingOverlay />;
  }

  if (error) {
    return (
      <div className="error-state">
        <p>Error loading content: {error}</p>
      </div>
    );
  }

  // Get sections from content
  const heroSection = content?.sections?.find(s => s.type === 'page-hero')?.content;
  const contactSection = content?.sections?.find(s => s.type === 'contact-details')?.content;

  return (
    <div className="contact-page">
      <SEO
        title="Contact Us"
        description="Get in touch with YOCILAB. Whether you're a young innovator, partner, or funder, we'd love to hear from you."
      />
      {/* Notification */}
      <Notification
        message={notification.message}
        type={notification.type}
        isVisible={notification.show}
        onClose={hideNotification}
        duration={5000}
      />

      {/* Hero Section */}
      <div className="contact-hero-section">
        <div style={containerStyle}>
          <div className="contact-hero-content">
            <h1 className="contact-hero-title">
              {heroSection?.mainTitle || "Get in Touch"}
            </h1>
            <p className="contact-hero-description">
              {heroSection?.description || "Ready to collaborate, innovate, or learn more about YOCILAB? We'd love to hear from you."}
            </p>
          </div>
        </div>
      </div>

      {/* Contact Content */}
      <div className="contact-content-section">
        <div style={containerStyle}>
          <div className="contact-content-grid">
            {/* Contact Information */}
            <div className="contact-info">
              <h2 className="contact-info-title">Let's Connect</h2>
              <p className="contact-info-description">
                Whether you're a young innovator, potential partner, or simply curious
                about our work, we're here to help and collaborate.
              </p>

              <div className="contact-info-items">
                <div className="contact-info-item">
                  <div className="contact-icon location-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                  </div>
                  <div className="contact-info-content">
                    <h3>Our Location</h3>
                    <p>Yaoundé, Cameroon<br />Central Africa</p>
                  </div>
                </div>

                <div className="contact-info-item">
                  <div className="contact-icon email-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                  </div>
                  <div className="contact-info-content">
                    <h3>Email Us</h3>
                    <p>info@yocilab.org<br />partnerships@yocilab.org</p>
                  </div>
                </div>

                <div className="contact-info-item">
                  <div className="contact-icon phone-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                  </div>
                  <div className="contact-info-content">
                    <h3>Call Us</h3>
                    <p>+237 XXX XXX XXX<br />Monday - Friday, 9AM - 6PM</p>
                  </div>
                </div>

                <div className="contact-info-item">
                  <div className="contact-icon social-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                    </svg>
                  </div>
                  <div className="contact-info-content">
                    <h3>Follow Us</h3>
                    <div className="social-links">
                      <a href="#" className="social-link">Facebook</a>
                      <a href="#" className="social-link">LinkedIn</a>
                      <a href="#" className="social-link">Twitter</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="contact-form-container">
              <h2 className="contact-form-title">Send us a Message</h2>
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name" className="form-label">
                    Full Name <span className="form-required">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`form-input ${formErrors.name ? 'form-input-error' : ''}`}
                    disabled={isSubmitting}
                    placeholder="Enter your full name"
                    aria-describedby={formErrors.name ? 'name-error' : null}
                  />
                  {formErrors.name && (
                    <div id="name-error" className="form-error" role="alert">
                      {formErrors.name}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    Email Address <span className="form-required">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`form-input ${formErrors.email ? 'form-input-error' : ''}`}
                    disabled={isSubmitting}
                    placeholder="Enter your email address"
                    aria-describedby={formErrors.email ? 'email-error' : null}
                  />
                  {formErrors.email && (
                    <div id="email-error" className="form-error" role="alert">
                      {formErrors.email}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="subject" className="form-label">
                    Subject <span className="form-required">*</span>
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className={`form-input ${formErrors.subject ? 'form-input-error' : ''}`}
                    disabled={isSubmitting}
                    aria-describedby={formErrors.subject ? 'subject-error' : null}
                  >
                    <option value="">Select a subject</option>
                    <option value="general">General Inquiry</option>
                    <option value="partnership">Partnership Opportunity</option>
                    <option value="programs">Programs & Services</option>
                    <option value="funding">Funding & Investment</option>
                    <option value="media">Media & Press</option>
                    <option value="careers">Careers & Opportunities</option>
                  </select>
                  {formErrors.subject && (
                    <div id="subject-error" className="form-error" role="alert">
                      {formErrors.subject}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="form-label">
                    Message <span className="form-required">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className={`form-textarea ${formErrors.message ? 'form-input-error' : ''}`}
                    rows="6"
                    disabled={isSubmitting}
                    placeholder="Tell us about your inquiry, partnership opportunity, or how we can help..."
                    aria-describedby={formErrors.message ? 'message-error' : null}
                  ></textarea>
                  {formErrors.message && (
                    <div id="message-error" className="form-error" role="alert">
                      {formErrors.message}
                    </div>
                  )}
                  <div className="form-hint">
                    {formData.message.length}/1000 characters
                  </div>
                </div>

                <button
                  type="submit"
                  className={`contact-submit-btn ${isSubmitting ? 'loading' : ''}`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <svg className="btn-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 12a9 9 0 11-6.219-8.56" />
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="22" y1="2" x2="11" y2="13" />
                        <polygon points="22,2 15,22 11,13 2,9 22,2" />
                      </svg>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Map Section */}
      <div className="contact-map-section">
        <div style={containerStyle}>
          <div className="contact-map-content">
            <h2 className="contact-map-title">Visit Our Innovation Hub</h2>
            <p className="contact-map-description">
              Located in the heart of Yaoundé, our innovation hub is where young
              changemakers come together to create, collaborate, and build the future.
            </p>
            <div className="contact-map-placeholder">
              <div className="map-placeholder-content">
                <div className="map-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <h3>YOCILAB Innovation Hub</h3>
                <p>Yaoundé, Cameroon</p>
                <button className="directions-btn">Get Directions</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="contact-cta-section">
        <div style={containerStyle}>
          <div className="contact-cta-content">
            <h2 className="contact-cta-title">Ready to Make an Impact?</h2>
            <p className="contact-cta-description">
              Join our community of young innovators and changemakers working
              to build a better future for Africa.
            </p>
            <div className="contact-cta-buttons">
              <button className="cta-primary-btn">Join Our Community</button>
              <button className="cta-secondary-btn">Explore Programs</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
